import { apiSlice } from "../../app/api/apiSlice";

export const tenantsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTenants: builder.query({
      query: () => "/tenant",
      // keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: "Tenant", id: _id })),
              { type: "Tenant", id: "LIST" },
            ]
          : ["Tenant"],
    }),
    getTenantInformation: builder.query({
      query: () => "/tenant/information",
      // keepUnusedDataFor: 5,
      providesTags: ["Tenant"],
    }),
    getTenantSubscriptionTerminationPreview: builder.query({
      query: () => "/tenant/subscription/termination-preview",
      providesTags: ["Tenant"],
    }),
    getTenantPlan: builder.query({
      query: () => "/tenant/plan",
      providesTags: ["Tenant"],
    }),
    getTenantTestingSettings: builder.query({
      query: () => "/tenant/testing-settings",
      providesTags: ["Tenant"],
    }),
    addNewTenant: builder.mutation({
      query: (tenant) => ({
        url: "/tenant",
        method: "POST",
        body: {
          ...tenant,
        },
        invalidatesTags: ["Tenant"],
      }),
    }),
    updateTenant: builder.mutation({
      query: (data) => ({
        url: "/tenant",
        method: "PATCH",
        body: { ...data },
        invalidatesTags: ["Tenant"],
      }),
    }),
    updateTenantLogo: builder.mutation({
      query: (data) => ({
        url: "/tenant/change-logo",
        method: "POST",
        body: { ...data }, // logoName
        invalidatesTags: ["Tenant"],
      }),
    }),
    updateTenantMailSettings: builder.mutation({
      query: (mailSettings) => ({
        url: "/tenant/mail-settings",
        method: "PATCH",
        body: { mailSettings },
        invalidatesTags: ["Tenant"],
      }),
    }),
    updateTenantTestingSettings: builder.mutation({
      query: (fees) => ({
        url: "/tenant/testing-settings",
        method: "PATCH",
        body: { fees },
        invalidatesTags: ["Tenant"],
      }),
    }),
    updateTenantBilling: builder.mutation({
      query: (billingData) => ({
        url: "/tenant/billing",
        method: "PATCH",
        body: billingData,
        invalidatesTags: ["Tenant"],
      }),
    }),
    terminateTenantSubscription: builder.mutation({
      query: (data) => ({
        url: "/tenant/subscription/terminate",
        method: "POST",
        body: { ...data },
      }),
      invalidatesTags: [
        "Tenant",
        { type: "Subscription", id: "LIST" },
      ],
    }),
    revokeTenantSubscriptionTermination: builder.mutation({
      query: () => ({
        url: "/tenant/subscription/revoke-termination",
        method: "POST",
      }),
      invalidatesTags: [
        "Tenant",
        { type: "Subscription", id: "LIST" },
      ],
    }),
    sendTenantTestMail: builder.mutation({
      query: (payload) => {
        const payloadObject =
          payload && typeof payload === "object" && !Array.isArray(payload)
            ? payload
            : {};
        const nestedRecipientPayload =
          payloadObject.recipient &&
          typeof payloadObject.recipient === "object" &&
          !Array.isArray(payloadObject.recipient)
            ? payloadObject.recipient
            : null;

        const recipient =
          typeof payload === "string"
            ? payload
            : typeof payloadObject.recipient === "string"
              ? payloadObject.recipient
              : typeof nestedRecipientPayload?.recipient === "string"
                ? nestedRecipientPayload.recipient
                : "";

        const settings =
          payloadObject.settings ||
          payloadObject.mailSettings ||
          nestedRecipientPayload?.settings ||
          nestedRecipientPayload?.mailSettings;

        return {
          url: "/tenant/mail-test",
          method: "POST",
          body: { recipient, ...(settings ? { mailSettings: settings } : {}) },
        };
      },
    }),
    deleteTenant: builder.mutation({
      query: (id) => ({
        url: `/tenant/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Tenant", id: "LIST" }],
    }),
    uploadMailHtmlTemplate: builder.mutation({
      query: ({ templateType, htmlContent }) => ({
        url: "/tenant/mail-html-template",
        method: "POST",
        body: { templateType, htmlContent },
      }),
      invalidatesTags: ["Tenant"],
    }),
    deleteMailHtmlTemplate: builder.mutation({
      query: ({ templateType }) => ({
        url: "/tenant/mail-html-template",
        method: "DELETE",
        body: { templateType },
      }),
      invalidatesTags: ["Tenant"],
    }),
    updateTenantShopDelivery: builder.mutation({
      query: (shopDelivery) => ({
        url: "/tenant/shop-delivery",
        method: "PATCH",
        body: { shopDelivery },
      }),
      invalidatesTags: ["Tenant"],
    }),
    testShopDeliveryWebhook: builder.mutation({
      query: () => ({
        url: "/tenant/shop-delivery/test",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetTenantsQuery,
  useGetTenantInformationQuery,
  useGetTenantSubscriptionTerminationPreviewQuery,
  useGetTenantPlanQuery,
  useGetTenantTestingSettingsQuery,
  useUpdateTenantMutation,
  useUpdateTenantLogoMutation,
  useUpdateTenantMailSettingsMutation,
  useUpdateTenantTestingSettingsMutation,
  useUpdateTenantBillingMutation,
  useTerminateTenantSubscriptionMutation,
  useRevokeTenantSubscriptionTerminationMutation,
  useSendTenantTestMailMutation,
  useDeleteTenantMutation,
  useUploadMailHtmlTemplateMutation,
  useDeleteMailHtmlTemplateMutation,
  useUpdateTenantShopDeliveryMutation,
  useTestShopDeliveryWebhookMutation,
} = tenantsApiSlice;
